/**
 * Data layer for the social routes — `/user/:username`, `/connections/:kind`,
 * `/people-search` (web/docs/data-layer.md). Mirrors the mobile real client
 * (`app/src/data/api-real.ts` — shapes only, nothing imported):
 *
 * - `GET /v1/users/{username}` → the public card + the caller's connection
 *   state; `404` → `null` (unknown user), never a crash.
 * - `GET /v1/users/{username}/stats|activity|library` → the viewable-profile
 *   surfaces (same wire contracts as `/v1/stats` and `/v1/home` rows). The
 *   screen enables them only when `can_view_library` is true, so a `403`
 *   degrades to the error state; a `404` degrades to empty like the app.
 * - `GET /v1/connections/followers|following|requests` → cursor pages.
 * - `GET /v1/users/search` → prefix search over usernames + display names.
 * - Follow / unfollow / approve / decline are optimistic where it reads well:
 *   patch in `onMutate`, roll back in `onError`, invalidate in `onSettled`,
 *   toast + Retry on failure (web has no persisted mutation queue).
 */

import { useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiDelete, apiGet, apiPost, ApiError } from '@/api/http';
import { translate } from '@/i18n/locale';

import type {
  ApiApproveResponse,
  ApiConnectionsPage,
  ApiDeclineResponse,
  ApiFollowResponse,
  ApiStats,
  ApiUserActivityPage,
  ApiUserCard,
  ApiUserLibraryPage,
  ApiUserSearchPage,
} from '@shared/api-types';
import {
  mapConnectionUser,
  mapHomeShowToTitle,
  mapLibraryStats,
  mapStats,
  mapUserActivityItem,
  mapUserCard,
  mapUserSearchResult,
} from '@shared/mappers';
import type {
  ConnectionUser,
  ConnectionsPage,
  FollowResult,
  LibraryItem,
  LibraryStats,
  Stats,
  UserActivityPage,
  UserCard,
  UserLibraryPage,
  UserSearchPage,
  UserSearchResult,
} from '@shared/types';

export type ConnectionKind = 'followers' | 'following' | 'requests';

/** Query keys are lower-cased — profile lookup is case-insensitive. */
const norm = (username: string) => username.trim().toLowerCase();

export const userKey = (username: string) => ['user', norm(username)] as const;
export const userStatsKey = (username: string) => ['userStats', norm(username)] as const;
export const userActivityKey = (username: string) => ['userActivity', norm(username)] as const;
export const userLibraryKey = (username: string) => ['userLibrary', norm(username)] as const;
export const connectionsKey = (kind: ConnectionKind) => ['connections', kind] as const;
export const peopleSearchPrefix = ['peopleSearch'] as const;
export const peopleSearchKey = (term: string) => ['peopleSearch', term] as const;

const enc = encodeURIComponent;

/* --- reads ------------------------------------------------------------------ */

/** `GET /v1/users/{username}`; `null` when the username is unknown (404). */
export async function fetchUserCard(username: string): Promise<UserCard | null> {
  try {
    const card = await apiGet<ApiUserCard>(`/v1/users/${enc(username)}`);
    return mapUserCard(card);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** `GET /v1/users/{username}/stats` — identical wire contract to `/v1/stats`. */
export async function fetchUserStats(username: string): Promise<Stats> {
  const stats = await apiGet<ApiStats>(`/v1/users/${enc(username)}/stats`);
  return mapStats(stats);
}

/**
 * `GET /v1/users/{username}/activity` — one newest-first page (the public
 * shelf keeps the feed compact; the app fetches a single page too).
 */
export async function fetchUserActivity(username: string): Promise<UserActivityPage> {
  try {
    const r = await apiGet<ApiUserActivityPage>(`/v1/users/${enc(username)}/activity`, {
      query: { per_page: 12 },
    });
    return { items: (r.items ?? []).map(mapUserActivityItem), cursor: r.cursor ?? undefined };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { items: [] };
    throw err;
  }
}

const EMPTY_LIBRARY_STATS: LibraryStats = {
  watching: 0,
  completed: 0,
  watchlist: 0,
  dropped: 0,
  total: 0,
};

/** `GET /v1/users/{username}/library` — one cursor page of read-only rows. */
export async function fetchUserLibraryPage(
  username: string,
  cursor?: string,
): Promise<UserLibraryPage> {
  const query: Record<string, string | number | undefined> = { per_page: 20 };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiUserLibraryPage>(`/v1/users/${enc(username)}/library`, { query });
    return {
      username: r.username,
      publicUserId: r.public_user_id,
      stats: mapLibraryStats(r.stats),
      items: (r.items ?? []).map((s) => {
        const title = mapHomeShowToTitle(s);
        return {
          title,
          status: title.userStatus ?? 'watching',
          userRating: title.userRating ?? null,
          watchedCount: Math.round(s.watched_episodes ?? 0),
          totalEpisodes: Math.round(s.total_episodes ?? s.number_of_episodes ?? 0),
          rewatchCount: title.rewatchCount ?? 0,
        } as LibraryItem;
      }),
      cursor: r.cursor ?? undefined,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { username, publicUserId: '', stats: EMPTY_LIBRARY_STATS, items: [] };
    }
    throw err;
  }
}

/** `GET /v1/connections/{kind}` — one cursor page (30 rows, like the app). */
export async function fetchConnectionsPage(
  kind: ConnectionKind,
  cursor?: string,
): Promise<ConnectionsPage> {
  const query: Record<string, string | number | undefined> = { per_page: 30 };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiConnectionsPage>(`/v1/connections/${kind}`, { query });
    return { users: (r.users ?? []).map(mapConnectionUser), cursor: r.cursor ?? undefined };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { users: [] };
    throw err;
  }
}

/** `GET /v1/users/search` — one cursor page of prefix matches. */
export async function fetchPeopleSearchPage(term: string, cursor?: string): Promise<UserSearchPage> {
  const query: Record<string, string | number | undefined> = { q: term, per_page: 20 };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiUserSearchPage>('/v1/users/search', { query });
    return {
      users: (r.users ?? []).map(mapUserSearchResult),
      query: r.query ?? term,
      cursor: r.cursor ?? undefined,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { users: [], query: term };
    throw err;
  }
}

/* --- query hooks ------------------------------------------------------------- */

export function useUserCard(username: string) {
  return useQuery({
    queryKey: userKey(username),
    queryFn: () => fetchUserCard(username),
    enabled: username.trim().length > 0,
  });
}

/** Enabled only for viewable profiles — private gates every surface together. */
export function useUserStats(username: string, enabled: boolean) {
  return useQuery({
    queryKey: userStatsKey(username),
    queryFn: () => fetchUserStats(username),
    enabled: enabled && username.trim().length > 0,
  });
}

export function useUserActivity(username: string, enabled: boolean) {
  return useQuery({
    queryKey: userActivityKey(username),
    queryFn: () => fetchUserActivity(username),
    enabled: enabled && username.trim().length > 0,
  });
}

export function useUserLibrary(username: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: userLibraryKey(username),
    queryFn: ({ pageParam }) => fetchUserLibraryPage(username, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
    enabled: enabled && username.trim().length > 0,
  });
}

export function useConnections(kind: ConnectionKind) {
  return useInfiniteQuery({
    queryKey: connectionsKey(kind),
    queryFn: ({ pageParam }) => fetchConnectionsPage(kind, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
  });
}

/** Enabled once the debounced term is non-empty (the endpoint 400s on `q=`). */
export function usePeopleSearch(term: string) {
  return useInfiniteQuery({
    queryKey: peopleSearchKey(term),
    queryFn: ({ pageParam }) => fetchPeopleSearchPage(term, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
    enabled: term.length > 0,
  });
}

/** Flatten + dedupe an infinite library query (a retried page can overlap). */
export function flattenLibraryPages(
  data: InfiniteData<UserLibraryPage> | undefined,
): LibraryItem[] {
  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.title.id)) return false;
    seen.add(item.title.id);
    return true;
  });
}

export function flattenConnectionPages(
  data: InfiniteData<ConnectionsPage> | undefined,
): ConnectionUser[] {
  const users = data?.pages.flatMap((page) => page.users) ?? [];
  const seen = new Set<string>();
  return users.filter((user) => {
    if (seen.has(user.publicUserId)) return false;
    seen.add(user.publicUserId);
    return true;
  });
}

export function flattenSearchPages(
  data: InfiniteData<UserSearchPage> | undefined,
): UserSearchResult[] {
  const users = data?.pages.flatMap((page) => page.users) ?? [];
  const seen = new Set<string>();
  return users.filter((user) => {
    if (seen.has(user.publicUserId)) return false;
    seen.add(user.publicUserId);
    return true;
  });
}

/* --- mutation plumbing -------------------------------------------------------- */

/**
 * Mutations with a toast + Retry on failure. The ref makes the Retry action
 * re-enter the same mutation (fresh onMutate/onError cycle) without closing
 * over a stale `mutate` (same wrapper as `features/show/api.ts`).
 */
function useToastedMutation<TData, TVariables, TContext>(
  options: UseMutationOptions<TData, Error, TVariables, TContext>,
  errorMessage: (vars: TVariables) => string,
) {
  const mutateRef = useRef<(vars: TVariables) => void>(() => {});
  const { onError, ...rest } = options;
  const mutation = useMutation<TData, Error, TVariables, TContext>({
    ...rest,
    onError: (error, vars, onMutateResult, context) => {
      onError?.(error, vars, onMutateResult, context);
      toast.error(errorMessage(vars), {
        action: { label: translate('common.retry'), onClick: () => mutateRef.current(vars) },
      });
    },
  });
  mutateRef.current = mutation.mutate;
  return mutation;
}

function patchUserCard(qc: QueryClient, username: string, patch: (card: UserCard) => UserCard) {
  qc.setQueryData<UserCard | null | undefined>(userKey(username), (old) =>
    old ? patch(old) : old,
  );
}

/** Patch the person's row in every cached people-search result page. */
function patchSearchResults(
  qc: QueryClient,
  username: string,
  patch: (row: UserSearchResult) => UserSearchResult,
) {
  const target = norm(username);
  for (const [key] of qc.getQueriesData<InfiniteData<UserSearchPage>>({
    queryKey: peopleSearchPrefix,
  })) {
    const data = qc.getQueryData<InfiniteData<UserSearchPage>>(key);
    if (!data) continue;
    qc.setQueryData(key, {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        users: page.users.map((row) =>
          norm(row.username) === target ? patch(row) : row,
        ),
      })),
    });
  }
}

/** Follow / unfollow also move rows in the caller's own connection lists. */
function invalidateSocialScopes(qc: QueryClient, username?: string) {
  if (username) void qc.invalidateQueries({ queryKey: userKey(username) });
  void qc.invalidateQueries({ queryKey: ['connections'] });
  void qc.invalidateQueries({ queryKey: ['myProfile'] });
  void qc.invalidateQueries({ queryKey: peopleSearchPrefix });
}

/**
 * Search rows are not snapshotted — an optimistic flip that fails converges
 * via the `onSettled` invalidation (ui-ux.md §7: failed mutations refetch).
 */
interface FollowSnapshot {
  card?: UserCard | null;
  following?: InfiniteData<ConnectionsPage>;
}

/* --- mutations ----------------------------------------------------------------- */

/**
 * `POST /v1/users/{username}/follow` — follows a public profile, sends a
 * request to a private one. Optimistic: the card + any cached search rows flip
 * immediately (private target → `requested`), reconciled by the response.
 */
export function useFollowUser() {
  const qc = useQueryClient();
  return useToastedMutation<FollowResult, string, FollowSnapshot>(
    {
      mutationFn: async (username: string): Promise<FollowResult> => {
        const r = await apiPost<ApiFollowResponse>(`/v1/users/${enc(username)}/follow`);
        return { following: r.following, requested: r.requested, applied: r.applied };
      },
      onMutate: async (username) => {
        await qc.cancelQueries({ queryKey: userKey(username) });
        const card = qc.getQueryData<UserCard | null>(userKey(username));
        // A private target becomes a pending request, not an active follow.
        const becomesRequested = !!card && card.isPrivate && !card.connection.following;
        patchUserCard(qc, username, (c) => ({
          ...c,
          connection: { ...c.connection, following: !becomesRequested, requested: becomesRequested },
          followersCount:
            c.canViewLibrary && !becomesRequested ? c.followersCount + 1 : c.followersCount,
        }));
        patchSearchResults(qc, username, (row) =>
          becomesRequested ? { ...row, iRequested: true } : { ...row, iFollow: true },
        );
        return { card };
      },
      onSuccess: (result, username) => {
        patchUserCard(qc, username, (c) => ({
          ...c,
          connection: { ...c.connection, following: result.following, requested: result.requested },
        }));
        patchSearchResults(qc, username, (row) => ({
          ...row,
          iFollow: result.following,
          iRequested: result.requested,
        }));
      },
      onError: (_err, username, ctx) => {
        if (ctx?.card !== undefined) qc.setQueryData(userKey(username), ctx.card);
      },
      onSettled: (_data, _err, username) => invalidateSocialScopes(qc, username),
    },
    (username) => translate('social.error.follow', { username }),
  );
}

/**
 * `DELETE /v1/users/{username}/follow` — unfollow, or cancel an outstanding
 * request. Optimistic: the edge clears everywhere it is cached, and the person
 * leaves the caller's following list (rolled back on error).
 */
export function useUnfollowUser() {
  const qc = useQueryClient();
  return useToastedMutation<void, string, FollowSnapshot>(
    {
      mutationFn: async (username: string): Promise<void> => {
        await apiDelete(`/v1/users/${enc(username)}/follow`);
      },
      onMutate: async (username) => {
        await qc.cancelQueries({ queryKey: userKey(username) });
        await qc.cancelQueries({ queryKey: connectionsKey('following') });
        const card = qc.getQueryData<UserCard | null>(userKey(username));
        const following = qc.getQueryData<InfiniteData<ConnectionsPage>>(
          connectionsKey('following'),
        );
        patchUserCard(qc, username, (c) => ({
          ...c,
          connection: { ...c.connection, following: false, requested: false },
          followersCount:
            c.canViewLibrary && c.connection.following
              ? Math.max(0, c.followersCount - 1)
              : c.followersCount,
        }));
        patchSearchResults(qc, username, (row) => ({ ...row, iFollow: false, iRequested: false }));
        if (following) {
          const target = norm(username);
          qc.setQueryData<InfiniteData<ConnectionsPage>>(connectionsKey('following'), {
            ...following,
            pages: following.pages.map((page) => ({
              ...page,
              users: page.users.filter(
                (user) => (user.username ? norm(user.username) : user.publicUserId) !== target,
              ),
            })),
          });
        }
        return { card, following };
      },
      onError: (_err, username, ctx) => {
        if (ctx?.card !== undefined) qc.setQueryData(userKey(username), ctx.card);
        if (ctx?.following !== undefined) {
          qc.setQueryData(connectionsKey('following'), ctx.following);
        }
      },
      onSettled: (_data, _err, username) => invalidateSocialScopes(qc, username),
    },
    (username) => translate('social.error.unfollow', { username }),
  );
}

export interface RespondVars {
  username: string;
  approve: boolean;
}

/**
 * `POST /v1/connections/requests/{username}/approve|decline`. Optimistic: the
 * row leaves the requests list immediately; approval refetches followers +
 * profile counters on settle (the server flips both mirrored edges).
 */
export function useRespondToRequest() {
  const qc = useQueryClient();
  return useToastedMutation<boolean, RespondVars, { requests?: InfiniteData<ConnectionsPage> }>(
    {
      mutationFn: async ({ username, approve }: RespondVars): Promise<boolean> => {
        const path = `/v1/connections/requests/${enc(username)}/${approve ? 'approve' : 'decline'}`;
        if (approve) {
          const r = await apiPost<ApiApproveResponse>(path);
          return r.approved;
        }
        const r = await apiPost<ApiDeclineResponse>(path);
        return r.declined;
      },
      onMutate: async ({ username }) => {
        await qc.cancelQueries({ queryKey: connectionsKey('requests') });
        const requests = qc.getQueryData<InfiniteData<ConnectionsPage>>(
          connectionsKey('requests'),
        );
        if (requests) {
          const target = norm(username);
          qc.setQueryData<InfiniteData<ConnectionsPage>>(connectionsKey('requests'), {
            ...requests,
            pages: requests.pages.map((page) => ({
              ...page,
              users: page.users.filter(
                (user) => (user.username ? norm(user.username) : user.publicUserId) !== target,
              ),
            })),
          });
        }
        return { requests };
      },
      onError: (_err, _vars, ctx) => {
        if (ctx?.requests !== undefined) qc.setQueryData(connectionsKey('requests'), ctx.requests);
      },
      onSettled: (_data, _err, vars) => invalidateSocialScopes(qc, vars?.username),
    },
    (vars) =>
      vars.approve ? translate('social.error.approve') : translate('social.error.decline'),
  );
}
